import { sendSentryError } from '@lib/sentry';
import { CONTENT_CATEGORY_ID } from '@/constants';
import { getItems, type Item } from './items';
import { FetchAPIOptions } from '@/services/api';

// ============================================
// Types
// ============================================

export interface ReanalysisCategoryData {
  key: string;
  value: number;
}

export interface ReanalysisData {
  chartData: number[];
  chartLastYear: number;
  chartLastMonth: number;
  chartYMax?: number;
  categories: ReanalysisCategoryData[];
}

// ============================================
// Parse post content
// ============================================

/**
 * WordPress 게시물의 content 필드에서 key: value 형식의 데이터를 파싱
 *
 * 예시 content:
 * chartData: 47,38,18,36,31,122,42,29,66,89,187,96
 * chartLastYear: 2025
 * chartLastMonth: 3
 * chartYMax: 200
 * categories: variantDb:717,newDisease:298,phenotype:105,technology:80,familyStudy:58
 */
function parseContentFields(content: string): Record<string, string> {
  const fields: Record<string, string> = {};

  // WordPress uses <br> between lines — convert to newlines, then strip remaining HTML
  const plainText = content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  for (const line of plainText.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const colonIndex = trimmed.indexOf(':');
    if (colonIndex === -1) continue;

    const key = trimmed.slice(0, colonIndex).trim();
    const value = trimmed.slice(colonIndex + 1).trim();
    if (key) {
      fields[key] = value;
    }
  }

  return fields;
}

function parseChartData(raw: string): number[] {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map(v => Number(v.trim()))
    .filter(v => !isNaN(v));
}

function parseCategories(raw: string): ReanalysisCategoryData[] {
  if (!raw || typeof raw !== 'string') return [];
  return raw.split(',').reduce<ReanalysisCategoryData[]>((acc, pair) => {
    const [key, valueStr] = pair.trim().split(':');
    const value = Number(valueStr);
    if (key && !isNaN(value)) {
      acc.push({ key: key.trim(), value });
    }
    return acc;
  }, []);
}

function transformPostToReanalysisData(post: Item): ReanalysisData | null {
  const fields = parseContentFields(post.content);

  const chartLastYear = Number(fields.chartLastYear);
  const chartLastMonth = Number(fields.chartLastMonth);

  if (!fields.chartData || !chartLastYear || !chartLastMonth) return null;

  return {
    chartData: parseChartData(fields.chartData),
    chartLastYear,
    chartLastMonth,
    chartYMax: Number(fields.chartYMax) || undefined,
    categories: parseCategories(fields.categories),
  };
}

// ============================================
// Main Function
// ============================================

export async function getReanalysisData(
  options?: FetchAPIOptions,
): Promise<ReanalysisData | null> {
  try {
    const items = await getItems(CONTENT_CATEGORY_ID.reanalysis, {
      refresh: options?.refresh ?? false,
      label: 'getReanalysisData',
      prodFilter: options?.prodFilter,
    });

    if (!items.length) return null;

    // 가장 최신 게시물 사용
    const latestPost = [...items].sort(
      (a, b) => Date.parse(b.date) - Date.parse(a.date),
    )[0];

    return transformPostToReanalysisData(latestPost);
  } catch (error) {
    sendSentryError({
      error,
      source: 'reanalysis-data-fetch',
    });
    return null;
  }
}
